import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import styled from "styled-components";
import BerthTariffTable from "../../components/BerthTariff/BerthTariffTable";
import Button from "../../components/Button";
import ManageTariff from "../../components/ManageTariff";
import ErrorSuccessModal from "../../components/Modal/ErrorSuccessModal";
import { ToastMessage } from "../../components/ToastMessage";
import { StyledPageHeader } from "../../components/YachtClub/YachtClubRegistration/YachtClubRegistrationHeader";
import { Colors, Text } from "../../config/Constants";
import { useLoadContext } from "../../contexts/LoadContext";
import {
  getFomattedTariffFromId,
  getFormattedBerthListData,
  getFormattedPostBerthData,
} from "../../helper";
import {
  getBerthTariffList,
  getTariffDataFromId,
  postTariffDetails,
  updateTariffDetails,
} from "../../services/yachtClubService";
import { validateBerthTariff } from "./validateBerthTariff";

export interface BerthTariffDataType {
  range: string;
  annual: string;
  monthly: string;
  daily: string;
  dropOrPickUp: string;
}

const BerthTariff = () => {
  const navigate = useNavigate();
  const [berthTariffList, setBerthTariffList] = useState([]);
  const [showManageTariff, setTariffForm] = useState(false);
  const [isTableDataLoading, setTableLoader] = useState(false);
  const [tariffId, setTariffId] = useState("");
  const [isFailureModal, setIsFailureModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState({
    heading: "",
    subHeading: "",
  });
  const [tariffData, setTariffData] = useState({
    range: "",
    annual: "",
    monthly: "",
    daily: "",
    dropOrPickUp: "",
  });
  const [error, setError] = useState({
    range: "",
    annual: "",
    monthly: "",
    daily: "",
    dropOrPickUp: "",
  });
  const [isActive, setIsActive] = useState(true);
  const [isSuccess, setIsSuccess] = useState(false);
  const [berthTariffAdded, setBerthTarifAdded] = useState(false);
  const { setLoader } = useLoadContext();

  const handleChange = (event: any) => {
    let formData = { ...tariffData };
    let formError = { ...error };
    //@ts-ignore
    formData[event.target.name] = event.target.value;
    //@ts-ignore
    formError[event.target.name] = "";
    setTariffData(formData);
    setError(formError);
  };

  const handleToggle = () => {
    setIsActive(!isActive);
  };

  const handleCancel = () => {
    setTariffForm(false);
  };

  const handleError = (error: any) => {
    if (navigator.onLine) {
      setIsFailureModal(true);
      setErrorMessage({
        heading: "Something went wrong.",
        subHeading: error.message,
      });
    } else {
      toast.error(
        <ToastMessage
          message="You're offline now"
          subMessage="Oops! Internet is disconnected."
        />,
        {
          hideProgressBar: true,
        }
      );
    }
  };

  const handleSave = async (data: any) => {
    setLoader(true);
    try {
      const response = await postTariffDetails(data);
      if (response.status === 201) {
        setLoader(false);
        setIsSuccess(true);
        setBerthTarifAdded(true);
      }
    } catch (error: any) {
      setLoader(false);
      if (navigator.onLine) {
        setIsFailureModal(true);
        setErrorMessage({
          heading: "Something went wrong.",
          subHeading: error.message,
        });
      }
    }
    setTariffForm(false);
  };

  const fetchTariffDataFromId = async () => {
    setLoader(true);
    try {
      let response = await getTariffDataFromId(tariffId);
      if (response.status === 200) {
        setLoader(false);
        setTariffData(getFomattedTariffFromId(response?.data?.data));
        setIsActive(response?.data?.data?.isActive);
      }
    } catch (error: any) {
      setLoader(false);
      if (navigator.onLine) {
        setIsFailureModal(true);
        setErrorMessage({
          heading: "Something went wrong.",
          subHeading: error.message,
        });
      }
    }
  };

  const onEditClick = (id: string) => {
    setTariffId(id);
    setTariffForm(true);
  };

  const fetchGetBerthTariffList = async () => {
    setTableLoader(true);
    try {
      const response = await getBerthTariffList();
      if (response.data.code === 200) {
        setTableLoader(false);
        setBerthTariffList(getFormattedBerthListData(response.data.data));
      }
    } catch (error: any) {
      setTableLoader(false);
      handleError(error);
    }
  };

  const updateTariffData = async (data: any) => {
    setLoader(true);
    try {
      let response = await updateTariffDetails(data, tariffId);
      if (response.status === 200) {
        setLoader(false);
        setIsSuccess(true);
        setBerthTarifAdded(true);
        setTariffId("");
      }
    } catch (error: any) {
      setLoader(false);
      if (navigator.onLine) {
        setIsFailureModal(true);
        setErrorMessage({
          heading: "Something went wrong.",
          subHeading: error.message,
        });
      }
    }
    setTariffForm(false);
  };

  const handleSubmitTariff = () => {
    if (!validateBerthTariff(tariffData, setError)) {
      setError({
        range: "",
        annual: "",
        monthly: "",
        daily: "",
        dropOrPickUp: "",
      });
      if (tariffId) {
        updateTariffData(getFormattedPostBerthData(tariffData, isActive));
      } else {
        handleSave(getFormattedPostBerthData(tariffData, isActive));
      }
      setTariffData({
        range: "",
        annual: "",
        monthly: "",
        daily: "",
        dropOrPickUp: "",
      });
    }
  };

  useEffect(() => {
    fetchGetBerthTariffList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [berthTariffAdded]);

  useEffect(() => {
    if (tariffId) {
      fetchTariffDataFromId();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tariffId]);

  return (
    <>
      <BackButtonWrapper>
        <StyledPageHeader>{Text.berthTariff}</StyledPageHeader>
        <Button
          label="Back"
          isRounded={true}
          width={"120px"}
          onClick={() => navigate("/admin/yacht-club")}
        />
      </BackButtonWrapper>
      <StyledBerthTariffHeader>
        <ButtonWrapper>
          <Button
            width="12.5rem"
            variant="filled"
            label={Text.addBerthTariff}
            color={Colors.red}
            onClick={() => {
              setTariffId("");
              setTariffForm(true);
              setTariffData({
                range: "",
                annual: "",
                monthly: "",
                daily: "",
                dropOrPickUp: "",
              });
              setIsActive(true);
            }}
          />
        </ButtonWrapper>
      </StyledBerthTariffHeader>
      {showManageTariff && (
        <ManageTariff
          id={tariffId}
          isEdit={tariffId ? true : false}
          tariffData={tariffData}
          onCancel={handleCancel}
          onSave={handleSubmitTariff}
          isActive={isActive}
          handleToggle={handleToggle}
          handleChange={handleChange}
          error={error}
        />
      )}
      <BerthTariffTable
        data={berthTariffList}
        onEditClick={onEditClick}
        isTableDataLoading={isTableDataLoading}
      />
      {isFailureModal && (
        <ErrorSuccessModal
          variant="error"
          heading={errorMessage.heading}
          subHeading={errorMessage.subHeading}
          buttonLabel="Ok"
          setOpenOrClose={setIsFailureModal}
          onSubmit={() => setIsFailureModal(false)}
        />
      )}
      {isSuccess && (
        <ErrorSuccessModal
          variant="success"
          heading="Saved successfully!"
          buttonLabel="Ok"
          setOpenOrClose={setIsSuccess}
          onSubmit={() => {
            setIsSuccess(false);
            setBerthTarifAdded(false);
            setIsActive(true);
          }}
        />
      )}
    </>
  );
};

export default BerthTariff;

const StyledBerthTariffHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 1.25rem;
  @media only screen and (max-width: 768px) {
    padding-bottom: 0;
  }
`;
const ButtonWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
`;
const BackButtonWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  padding-bottom: 1rem;
  align-items: center;
  @media only screen and (max-width: 768px) {
    padding-top: 1rem;
    justify-content: flex-end;
  }
`;
